/**
 * Module dependencies.
 */

import { AnimationController } from 'src/components/core/animations/animation-controller';
import { Button } from 'src/components/core/buttons/button';
import { FadeInUp } from 'src/components/core/animations/fade-in-up';
import { Grid, SubGrid } from 'src/components/core/layout/grid-content';
import { Heading } from 'src/components/core/heading';
import { PricingPack, Service } from 'src/components/core/pricing-pack';
import { Section } from 'src/components/core/layout/section';
import { media } from '@untile/react-core/styles/media';
import { routeResolve } from 'src/core/utils/routes';
import { useTranslation } from 'next-i18next';
import chevronSvg from 'src/assets/svgs/chevron-right.svg';
import iconPrice from 'src/assets/svgs/price.svg';
import styled from 'styled-components';

/**
 * `Props` type.
 */

type Props = {
  services: Service[];
};

/**
 * `StyledSubGrid` styled component.
 */

const StyledSubGrid = styled(SubGrid)`
  display: grid;
  grid-template-areas: 'heading' 'pricing';
  grid-template-columns: 1fr;

  ${media.min.ms`
    grid-column-gap: var(--space-xs);
    grid-template-areas: 'heading pricing' ;
    grid-template-columns: 244px 1fr;
  `}

  ${media.min.xl`
    grid-template-columns: 312px 1fr;
  `}

  ${media.min.xxl`
    grid-template-columns: 456px 1fr;
  `}
`;

/*
 * `PricingWrapper` styled component.
 */

const PricingWrapper = styled.div`
  display: flex;
  flex-direction: column;
  grid-area: pricing;
  width: 100%;

  ${media.min.ms`
    margin-left: auto;
    max-width: 840px;
  `}
`;

/*
 * Export `ServicePricing` component.
 */

export const ServicePricing = ({ services }: Props) => {
  const { t } = useTranslation();

  return (
    <AnimationController>
      <Section>
        <Grid>
          <StyledSubGrid>
            <Heading
              asTitle={'h3'}
              icon={iconPrice}
              offset
              style={{ gridArea: 'heading' }}
              title={t('common:pricing.title')}
              titleSize={28}
            />

            <PricingWrapper>
              <PricingPack services={services} />

              <FadeInUp style={{ margin: '0 auto' }}>
                <Button
                  aria-label={t('common:actions.checkPricesPage')}
                  colorTheme={'text'}
                  href={routeResolve('prices')}
                  icon={chevronSvg}
                  reverse
                  style={{ marginTop: 'var(--space-ms)' }}
                >
                  {t('common:actions.checkPricesPage')}
                </Button>
              </FadeInUp>
            </PricingWrapper>
          </StyledSubGrid>
        </Grid>
      </Section>
    </AnimationController>
  );
};
